import {
  findParentPageNotionId,
  isSubstantialDatabaseRowMd,
  parseNotionPageMd,
  requiredAncestorPageIds,
  sortByParentOrder,
  type EnrichedExportNode,
  type ExportCatalog,
} from '@couplespace/core/notion-export';

import { newBlockId } from '@/lib/blocks/ids';

import {
  postNotionImportBatch,
  type NotionImportDatabasePayload,
  type NotionImportPagePayload,
} from './api.web.js';
import { notionMarkdownToContentBlocks } from './markdown-to-content.js';
import { readPageMarkdown, readTextFile, resolveMdPath, type ExportFolderScan } from './read-export-folder.js';
import { resolveImagesInContentBlocks } from './resolve-images.js';
import {
  buildImportKindMap,
  buildPlannedIdMap,
  childNotionIdsByParent,
  rewriteNotionLinksInMarkdown,
  stripImportedChildLinks,
} from './rewrite-links.js';

export type ImportPlanItem =
  | {
      kind: 'page';
      notionId: string;
      parentNotionId: string | null;
      title: string;
      mdPath: string;
    }
  | {
      kind: 'database';
      notionId: string;
      parentNotionId: string | null;
      title: string;
      csvPath: string;
      rowPages: Array<{ notionId: string; title: string; mdPath: string }>;
    };

/**
 * Build the ordered import plan from user selections.
 * - Selected pages become page blocks (preserving parent nesting).
 * - Selected databases become database blocks + CSV-ingested rows.
 * - Missing page ancestors are included so nested items land under the right parent.
 * - Items are topologically sorted (parents before children).
 */
export function buildImportPlan(
  catalog: ExportCatalog,
  selectedPageIds: Set<string>,
  selectedDatabaseIds: Set<string>,
): ImportPlanItem[] {
  const ancestorPageIds = requiredAncestorPageIds(
    catalog,
    selectedPageIds,
    selectedDatabaseIds,
  );
  const effectivePageIds = new Set([...selectedPageIds, ...ancestorPageIds]);

  const plan: ImportPlanItem[] = [];

  for (const db of catalog.databases) {
    if (!selectedDatabaseIds.has(db.notionId) || !db.csvPath) {
      continue;
    }
    plan.push({
      kind: 'database',
      notionId: db.notionId,
      parentNotionId: findParentPageNotionId(db.notionId, catalog.enrichedRoots),
      title: db.displayTitle,
      csvPath: db.csvPath,
      rowPages: db.rowPages
        .filter((row): row is EnrichedExportNode & { notionId: string; href: string } =>
          Boolean(row.notionId && row.href),
        )
        .map((row) => ({
          notionId: row.notionId,
          title: row.displayTitle,
          mdPath: row.href,
        })),
    });
  }

  function walkPageTree(nodes: EnrichedExportNode[], ancestorSelected: boolean): void {
    for (const node of nodes) {
      const id = node.notionId;
      const selected = ancestorSelected || (id != null && effectivePageIds.has(id));
      if (selected && node.href && id && node.kind !== 'database_with_csv' && node.kind !== 'database_row_pages') {
        plan.push({
          kind: 'page',
          notionId: id,
          parentNotionId: findParentPageNotionId(id, catalog.enrichedRoots),
          title: node.displayTitle,
          mdPath: node.href,
        });
      }
      walkPageTree(node.children, selected);
    }
  }
  walkPageTree(catalog.pageTree, false);

  return sortByParentOrder(plan);
}

export { buildImportKindMap, buildPlannedIdMap, childNotionIdsByParent };

export async function planItemToBatchPayload(
  item: ImportPlanItem,
  scan: ExportFolderScan,
  idMap: Record<string, string>,
  kindMap: Record<string, 'page' | 'database'>,
  uploadBlockId: string,
  childrenByParent: Map<string, Set<string>> = new Map(),
): Promise<NotionImportPagePayload | NotionImportDatabasePayload> {
  if (item.kind === 'database') {
    const rowPageContent: NotionImportDatabasePayload['rowPageContent'] = [];
    const uploadFailures: string[] = [];

    for (const row of item.rowPages) {
      const resolvedMdPath = resolveMdPath(scan, row.mdPath, row.notionId);
      const md = (await readPageMarkdown(scan, row.mdPath, row.notionId)) ?? '';
      if (!isSubstantialDatabaseRowMd(md, row.title)) {
        continue;
      }

      const parsed = parseNotionPageMd(md);
      const placeholderId = newBlockId();
      const body = rewriteNotionLinksInMarkdown(parsed.body, idMap, kindMap);
      const { contentBlocks: rawBlocks, contentRootId } = await notionMarkdownToContentBlocks(
        body,
        placeholderId,
        row.title,
      );
      const { blocks: contentBlocks, uploadFailures: rowUploadFailures } =
        await resolveImagesInContentBlocks(
          scan,
          resolvedMdPath ?? row.mdPath,
          uploadBlockId,
          rawBlocks,
        );
      uploadFailures.push(...rowUploadFailures);
      rowPageContent.push({
        notionId: row.notionId,
        title: row.title,
        contentRootId,
        contentBlocks,
      });
    }

    return {
      kind: 'database',
      notionId: item.notionId,
      parentNotionId: item.parentNotionId,
      title: item.title,
      csvText: (await readTextFile(scan.files, item.csvPath)) ?? '',
      rowPageContent,
      uploadFailures,
    };
  }

  const resolvedMdPath = resolveMdPath(scan, item.mdPath, item.notionId);
  const md = (await readPageMarkdown(scan, item.mdPath, item.notionId)) ?? '';
  console.log('[CS-NOTION] page:resolve', {
    notionId: item.notionId,
    title: item.title,
    requestedMdPath: item.mdPath,
    resolvedMdPath,
    mdLength: md.length,
    mdHead: md.slice(0, 120),
  });
  const parsed = parseNotionPageMd(md);
  const placeholderId = newBlockId();
  const childIds = childrenByParent.get(item.notionId) ?? new Set<string>();
  let body = stripImportedChildLinks(parsed.body, childIds);
  body = rewriteNotionLinksInMarkdown(body, idMap, kindMap);
  const { contentBlocks: rawBlocks, contentRootId } = await notionMarkdownToContentBlocks(
    body,
    placeholderId,
    item.title,
  );
  const { blocks: contentBlocks, uploadFailures } = await resolveImagesInContentBlocks(
    scan,
    resolvedMdPath ?? item.mdPath,
    uploadBlockId,
    rawBlocks,
  );

  return {
    kind: 'page',
    notionId: item.notionId,
    parentNotionId: item.parentNotionId,
    title: item.title,
    contentRootId,
    contentBlocks,
    uploadFailures,
  };
}

export { postNotionImportBatch };
